import { isRecord } from "@atoms/atom-types";

/**
 * Failures of the transport that reached the client without the shape of
 * one: a closed socket ends a subscription with a plain
 * `Error("Socket closed …")`. Whoever receives it from the transport marks
 * it here; the error itself stays untouched.
 */
const markedConnectionFailures = new WeakSet<object>();

/**
 * What the WebSocket link and the socket say in the message of an error
 * when the connection closed or could not be opened.
 */
const CONNECTION_CLOSED_MESSAGES = [
  "Socket closed",
  "WebSocket connection failed",
  "Connection closed",
] as const;

/** Whether a message of the transport says that the connection is gone. */
export function isConnectionClosedMessage(message: string): boolean {
  return CONNECTION_CLOSED_MESSAGES.some((text) => message.includes(text));
}

/** Marks an error as the failure of the connection itself. */
export function markConnectionFailure(error: Error): void {
  markedConnectionFailures.add(error);
}

/**
 * Whether an error is the failure of the connection itself: it was marked
 * as one, or the transport gave up (an `ApolloError` with a `networkError`)
 * and no server error came with it. Its message is text of the client
 * library, not something to show.
 */
export function isConnectionFailure(error: unknown): boolean {
  if (!isRecord(error)) {
    return false;
  }
  if (markedConnectionFailures.has(error)) {
    return true;
  }
  if (!error.networkError) {
    return false;
  }
  const { graphQLErrors } = error;
  return !Array.isArray(graphQLErrors) || graphQLErrors.length === 0;
}
